import { useCallback, useLayoutEffect, useState } from 'react';

export type UseResizeObserverOptions = {
  /** Skip the per-instance window `resize` listener (ResizeObserver still fires). @default true */
  subscribeWindowResize?: boolean;
};

/** Observes an element's content size (`clientWidth`/`clientHeight`). Mirrors `useTruncationObserver`. */
export function useResizeObserver(options?: UseResizeObserverOptions): {
  resizeRef: (node: HTMLElement | null) => void;
  width: number;
  height: number;
} {
  const subscribeWindowResize = options?.subscribeWindowResize ?? true;
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [size, setSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });

  const resizeRef = useCallback((node: HTMLElement | null) => {
    setElement(node);
  }, []);

  useLayoutEffect(() => {
    if (!element) {
      return undefined;
    }

    const update = (): void => {
      setSize(prev =>
        prev.width === element.clientWidth &&
        prev.height === element.clientHeight
          ? prev
          : { width: element.clientWidth, height: element.clientHeight }
      );
    };

    update();
    const rafId = window.requestAnimationFrame(update);

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => update())
        : null;
    resizeObserver?.observe(element);

    if (subscribeWindowResize) {
      window.addEventListener('resize', update);
    }

    return () => {
      window.cancelAnimationFrame(rafId);
      resizeObserver?.disconnect();
      if (subscribeWindowResize) {
        window.removeEventListener('resize', update);
      }
    };
  }, [element, subscribeWindowResize]);

  return { resizeRef, width: size.width, height: size.height };
}
